import * as m from '@staiter/i18n';
import type { CostScopeKind } from '@staiter/shared';
import { useQuery } from '@tanstack/react-query';
// S6 — Cost workspace (material/labor/overhead layers).
import { createFileRoute } from '@tanstack/react-router';
import { useState } from 'react';
import { EmptyState, PageHeader, Table } from '~/components/data';
import { HeroMetric } from '~/components/domain';
import { Button, Input, Select } from '~/components/primitives';
import { ProvenanceChip } from '~/components/status';
import { fetchCostWorkspace } from '~/lib/api/workspaces';
import shared from './_auth._shared.module.css';
import styles from './_auth.cost.module.css';

export const Route = createFileRoute('/_auth/cost')({ component: CostWorkspace });

function CostWorkspace(): JSX.Element {
  const [kind, setKind] = useState<CostScopeKind>('period');
  const [id, setId] = useState<string>('');
  const [pending, setPending] = useState<{ kind: CostScopeKind; id: string } | null>(null);

  const query = useQuery({
    queryKey: ['cost', 'workspace', pending?.kind, pending?.id],
    queryFn: () => fetchCostWorkspace(pending!),
    enabled: !!pending?.id,
    staleTime: 30_000,
  });

  const data = query.data;

  return (
    <div className={shared.page}>
      <PageHeader title={m.cost_page_title()} description={m.cost_page_description()} />

      <div className={styles.selectorRow}>
        <Select
          label={m.cost_scope_label()}
          value={kind}
          onChange={(e) => setKind(e.target.value as CostScopeKind)}
          options={[
            { value: 'period', label: m.cost_scope_period() },
            { value: 'sku', label: m.cost_scope_sku() },
            { value: 'work_order', label: m.cost_scope_work_order() },
          ]}
        />
        <Input
          label={m.cost_value_label()}
          value={id}
          onChange={(e) => setId(e.target.value)}
          placeholder={
            kind === 'period'
              ? '2026-04'
              : kind === 'sku'
                ? m.cost_placeholder_sku()
                : m.cost_placeholder_wo()
          }
        />
        <Button
          onClick={() => {
            if (id.trim()) setPending({ kind, id: id.trim() });
          }}
          disabled={!id.trim()}
        >
          {m.cost_show()}
        </Button>
      </div>

      {!pending && (
        // FE-118: empty state CTA.
        <EmptyState
          title={m.cost_empty_title()}
          description={m.cost_empty_desc()}
          action={
            <Button
              variant="secondary"
              onClick={() => {
                const el = document.querySelector<HTMLInputElement>('input[type="text"], select');
                el?.focus();
              }}
            >
              {m.ui_otkryt_posledniy_period()}
            </Button>
          }
        />
      )}

      {pending && data && (
        <>
          <div className={styles.heroRow}>
            <HeroMetric
              label={m.cost_hero_material()}
              value={data.material ?? data.layers.material}
              hint={m.cost_hero_material_hint()}
            />
            <HeroMetric
              label={m.cost_hero_labor()}
              value={data.labor ?? data.layers.labor}
              hint={m.cost_hero_labor_hint()}
            />
            <HeroMetric
              label={m.cost_hero_overhead()}
              value={data.overhead ?? data.layers.overhead}
              hint={m.cost_hero_overhead_hint()}
            />
            <HeroMetric label={m.cost_hero_total()} value={data.total} />
          </div>

          <Table
            ariaLabel={m.cost_table_aria()}
            loading={query.isLoading}
            data={data.rows ?? []}
            rowKey={(r) => r.id}
            columns={[
              { id: 'label', header: m.cost_col_label(), accessor: (r) => r.label },
              { id: 'cat', header: m.cost_col_category(), accessor: (r) => r.category, mono: true },
              {
                id: 'qty',
                header: m.cost_col_qty(),
                accessor: (r) => (r.qty ? `${r.qty} ${r.uom ?? ''}` : '—'),
                mono: true,
                align: 'right',
              },
              {
                id: 'amount',
                header: m.cost_col_amount(),
                accessor: (r) => `${r.amount.amount} ${r.amount.currency}`,
                mono: true,
                align: 'right',
              },
            ]}
          />

          {data.evidence_refs.length > 0 && (
            <div className={styles.evidence}>
              <h3 className={shared.sectionTitle}>{m.cost_evidence_title()}</h3>
              <div className={styles.evidenceChips}>
                {data.evidence_refs.map((ref) => {
                  // ref может быть строкой (uuid) или объектом EvidenceRef.
                  const r =
                    typeof ref === 'string'
                      ? { id: ref, source: undefined, document_id: null, fetched_at: undefined }
                      : ref;
                  return (
                    <ProvenanceChip
                      key={r.id}
                      source={r.source}
                      document_id={r.document_id ?? null}
                      fetched_at={r.fetched_at}
                    />
                  );
                })}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
